import { useState } from 'react';
import { api, type Enrolment, type Role } from '../api';
import { Banner, Card, Empty, Loading, Modal, useAsync, when } from '../ui';

/**
 * Drivers who joined through the join link.
 *
 * The pilot's first step: a driver presses Join and leaves the details we'll
 * need later. Nothing on this list has been checked — that only happens if
 * inDrive tells us the driver qualified — so the screen says "joined", never
 * "verified", and shows the phone number in full (it is how inDrive's team
 * reaches a driver) with BVN and account number masked.
 */
export function Joined({ role }: { role: Role }) {
  const [search, setSearch] = useState('');
  const [linking, setLinking] = useState<Enrolment | null>(null);
  const [linkDriver, setLinkDriver] = useState('');
  const [linkReason, setLinkReason] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkNote, setLinkNote] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [applied, setApplied] = useState('');

  const { data, error, loading, reload } = useAsync(
    () => api.enrolments({ search: applied || undefined, limit: '200' }),
    [applied],
  );

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      const csv = await api.enrolmentsCsv();
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `joined-drivers-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  const rows = data?.enrolments ?? [];

  async function link() {
    if (!linking || !linkDriver.trim() || linkReason.trim().length < 10) return;
    setLinkBusy(true);
    try {
      const r = await api.linkSignup(linking.id, linkDriver.trim(), linkReason.trim());
      setLinkNote({ tone: 'ok', text: r.message });
      setLinking(null); setLinkDriver(''); setLinkReason('');
      reload();
    } catch (e) {
      setLinking(null);
      setLinkNote({ tone: 'danger', text: (e as Error).message });
    } finally {
      setLinkBusy(false);
    }
  }
  const joinLink = `${window.location.origin}/join`;

  return (
    <div className="stack">
      <Card flush>
        <div className="card-head">
          {data && (
            <span className="small">
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{data.total_phones.toLocaleString()}</strong>{' '}
              <span className="muted">
                {data.total_phones === 1 ? 'driver has' : 'drivers have'} joined
                {data.total_signups !== data.total_phones &&
                  ` · ${data.total_signups.toLocaleString()} sign-ups, as some joined more than once`}
              </span>
            </span>
          )}
          <form className="row grow" onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); }}>
            <input
              type="search"
              placeholder="Phone number, last digits, or driver id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn" type="submit">Search</button>
            {applied && <button className="btn ghost" type="button" onClick={() => { setSearch(''); setApplied(''); }}>Clear</button>}
          </form>
          <button className="btn" type="button" onClick={() => void exportCsv()} disabled={exporting || !data || data.total_signups === 0}>
            {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>

        {linkNote && <div style={{ padding: 18 }}><Banner tone={linkNote.tone} title={linkNote.tone === 'ok' ? 'Linked' : 'Could not link'}>{linkNote.text}</Banner></div>}
        {error && <div style={{ padding: 18 }}><Banner tone="danger" title="Could not load joined drivers">{error}</Banner></div>}
        {exportError && <div style={{ padding: 18 }}><Banner tone="danger" title="Could not export">{exportError}</Banner></div>}
        {loading && <Loading />}

        {!loading && data && (rows.length === 0 ? (
          <Empty
            title={applied ? 'No one matches that search' : 'No one has joined yet'}
            hint={applied ? 'Search by the phone number the driver joined with (the last few digits will do), or their inDrive driver id.' : `Drivers appear here after they use the join link: ${joinLink}`}
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Joined</th>
                  <th>Phone</th>
                  <th>Driver id</th>
                  <th>BVN</th>
                  <th>Pays to</th>
                  <th>NIN</th>
                  {role === 'zeeh_admin' && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => <Row key={r.id} r={r} onLink={role === 'zeeh_admin' ? () => setLinking(r) : undefined} />)}
              </tbody>
            </table>
          </div>
        ))}

        <div className="card-head" style={{ borderBottom: 'none', borderTop: '1px solid var(--rule)' }}>
          <span className="small muted">
            Nothing here has been checked yet. Details are only verified if inDrive tells us the driver qualified.
            The export lists every sign-up, not just this page, and each download is recorded in the audit log.
            Join link: <span className="mono">{joinLink}</span>
          </span>
        </div>
      </Card>

      {linking && (
        <Modal
          title="Link this sign-up to a driver?"
          description="For a driver who joined under a different number than inDrive holds, so their referral could not find them."
          onClose={() => { setLinking(null); setLinkDriver(''); setLinkReason(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setLinking(null); setLinkDriver(''); setLinkReason(''); }}>Cancel</button>
              <button className="btn danger" disabled={linkBusy || !linkDriver.trim() || linkReason.trim().length < 10} onClick={link}>
                {linkBusy ? <span className="spin" /> : 'Link and record'}
              </button>
            </>
          }
        >
          <dl className="dl">
            <dt>Sign-up phone</dt><dd className="mono">{linking.phone}</dd>
            <dt>Pays to</dt><dd>{linking.paid_to} <span className="mono muted">{linking.account_masked}</span></dd>
          </dl>
          <div className="field">
            <label htmlFor="link-driver">inDrive driver ID</label>
            <input id="link-driver" value={linkDriver} onChange={(e) => setLinkDriver(e.target.value)} placeholder="the driver's ID from inDrive's list, e.g. 363855017" />
          </div>
          <div className="field">
            <label htmlFor="link-reason">What connects this sign-up to that driver?</label>
            <textarea
              id="link-reason"
              value={linkReason}
              onChange={(e) => setLinkReason(e.target.value)}
              placeholder="e.g. The BVN name matches inDrive's name for this driver; the sign-up phone is the BVN-registered number."
            />
            <span className="hint">At least 10 characters. Your name and this reason are written to the audit log permanently.</span>
          </div>
          <p className="small" style={{ lineHeight: 1.6 }}>
            The BVN is still checked at NIBSS, and KYC still checks the account holder's name, sanctions and fraud. Nothing is paid by this.
          </p>
        </Modal>
      )}
    </div>
  );
}

function Row({ r, onLink }: { r: Enrolment; onLink?: () => void }) {
  return (
    <tr>
      <td className="small">{when(r.joined_at)}</td>
      <td className="mono small">{r.phone}</td>
      <td className="mono small">{r.driver_id ?? <span className="muted">not given</span>}</td>
      <td className="mono small muted">{r.bvn_masked}</td>
      <td className="small">
        <div>{r.paid_to}</div>
        <div className="mono muted">{r.account_masked}</div>
      </td>
      <td className="small">{r.nin_supplied ? 'Given' : <span className="muted">—</span>}</td>
      {onLink && <td className="right"><button className="btn sm" onClick={onLink}>Link to a driver…</button></td>}
    </tr>
  );
}
